/**
 * 메뉴 목록 페이지 컴포넌트
 * 
 * 제휴사 Admin 소스를 본사에 맞게 적용
 * 메뉴 목록 조회, 계층 구조 표시, CRUD 기능 제공
 * 
 * 작성일: 2025-12-01
 */

import React, { useState } from 'react';
import { Card, Table, Form, Button, Badge, Modal } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { getMenus, deleteMenu, updateMenuOrder, updateSectionOrder, createMenu, updateMenu } from '../../services/menuService';
import type { Menu, CreateMenuRequest, UpdateMenuRequest } from '../../types/menu';
import { useAuth } from '../../contexts/AuthContext';
import { getIcon } from '../../utils/iconLoader';
import './Menus.css';

const Menus: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentAdmin } = useAuth();
  const [sectionFilter, setSectionFilter] = useState<string>('');
  const [expandedMenus, setExpandedMenus] = useState<Set<number>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState<Menu | null>(null);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [formData, setFormData] = useState<CreateMenuRequest>({
    section: 'DASHBOARD',
    path: '',
    label: '',
    icon: null,
    parent_id: null,
    sort_order: 0,
    is_active: true,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['menus', sectionFilter],
    queryFn: () =>
      getMenus({
        section: sectionFilter || undefined,
        is_active: undefined, // 모든 메뉴 조회
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteMenu(id),
    onSuccess: () => {
      // 모든 menus 관련 쿼리 무효화 (사이드바 포함)
      queryClient.invalidateQueries({ queryKey: ['menus'], exact: false });
      queryClient.refetchQueries({ queryKey: ['menus'], exact: false });
      setShowDeleteModal(false);
      setMenuToDelete(null);
      alert('메뉴가 삭제되었습니다.');
    },
    onError: (error: any) => {
      alert(`삭제 실패: ${error.response?.data?.error?.message || error.message}`);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMenuRequest) => createMenu(data),
    onSuccess: () => {
      // 모든 menus 관련 쿼리 무효화 (사이드바 포함)
      queryClient.invalidateQueries({ queryKey: ['menus'], exact: false });
      queryClient.refetchQueries({ queryKey: ['menus'], exact: false });
      setShowEditModal(false);
      resetForm();
      alert('메뉴가 생성되었습니다.');
    },
    onError: (error: any) => {
      alert(`생성 실패: ${error.response?.data?.error?.message || error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenuRequest }) => updateMenu(id, data),
    onSuccess: () => {
      // 모든 menus 관련 쿼리 무효화 (사이드바 포함)
      queryClient.invalidateQueries({ queryKey: ['menus'], exact: false });
      queryClient.refetchQueries({ queryKey: ['menus'], exact: false });
      setShowEditModal(false);
      setEditingMenu(null);
      resetForm();
      alert('메뉴가 수정되었습니다.');
    },
    onError: (error: any) => {
      alert(`수정 실패: ${error.response?.data?.error?.message || error.message}`);
    },
  });

  const updateOrderMutation = useMutation({
    mutationFn: (orders: Array<{ id: number; sort_order: number }>) => updateMenuOrder({ orders }),
    onSuccess: () => {
      // 모든 menus 관련 쿼리 무효화 및 즉시 refetch (사이드바 포함)
      queryClient.invalidateQueries({ queryKey: ['menus'], exact: false });
      queryClient.refetchQueries({ queryKey: ['menus'], exact: false });
    },
  });

  const updateSectionOrderMutation = useMutation({
    mutationFn: ({ section, direction }: { section: string; direction: 'up' | 'down' }) =>
      updateSectionOrder(section, direction),
    onSuccess: () => {
      // 모든 menus 관련 쿼리 무효화 및 즉시 refetch (사이드바 포함)
      queryClient.invalidateQueries({ queryKey: ['menus'], exact: false });
      queryClient.refetchQueries({ queryKey: ['menus'], exact: false });
    },
  });

  const resetForm = () => {
    setFormData({
      section: 'DASHBOARD',
      path: '',
      label: '',
      icon: null,
      parent_id: null,
      sort_order: 0,
      is_active: true,
    });
    setEditingMenu(null);
  };

  /**
   * 메뉴 토글 (계층 구조 확장/축소)
   */
  const toggleMenu = (menuId: number) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  /**
   * 섹션 한글 표시
   */
  const getSectionLabel = (section: string): string => {
    switch (section) {
      case 'DASHBOARD':
        return '대시보드';
      case 'MANAGEMENT':
        return '관리';
      case 'CONTENT':
        return '콘텐츠';
      case 'SYSTEM':
        return '시스템';
      default:
        return section;
    }
  };

  /**
   * 메뉴 삭제 확인
   */
  const handleDeleteClick = (menu: Menu) => {
    setMenuToDelete(menu);
    setShowDeleteModal(true);
  };

  /**
   * 메뉴 삭제 실행
   */
  const handleDeleteConfirm = () => {
    if (menuToDelete) {
      deleteMutation.mutate(menuToDelete.id);
    }
  };

  /**
   * 메뉴 생성
   */
  const handleCreate = () => {
    resetForm();
    setShowEditModal(true);
  };

  /**
   * 메뉴 수정
   */
  const handleEdit = (menu: Menu) => {
    setEditingMenu(menu);
    setFormData({
      section: menu.section,
      path: menu.path,
      label: menu.label,
      icon: menu.icon || null,
      parent_id: menu.parent_id,
      sort_order: menu.sort_order,
      is_active: menu.is_active,
    });
    setShowEditModal(true);
  };

  /**
   * 폼 제출
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingMenu) {
      updateMutation.mutate({ id: editingMenu.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  /**
   * 섹션 단위 이동 (위로)
   */
  const handleSectionMoveUp = (section: string) => {
    updateSectionOrderMutation.mutate({ section, direction: 'up' });
  };

  /**
   * 섹션 단위 이동 (아래로)
   */
  const handleSectionMoveDown = (section: string) => {
    updateSectionOrderMutation.mutate({ section, direction: 'down' });
  };

  /**
   * 모든 최상위 메뉴를 평면화하여 반환 (섹션 무관)
   */
  const flattenAllTopLevelMenus = (menuList: Menu[]): Menu[] => {
    const result: Menu[] = [];
    const flatten = (menus: Menu[]) => {
      menus.forEach((menu) => {
        if (!menu.parent_id) {
          // 최상위 메뉴만 추가
          result.push(menu);
        }
        if (menu.children && menu.children.length > 0) {
          flatten(menu.children);
        }
      });
    };
    flatten(menuList);
    return result;
  };

  /**
   * 메뉴 순서 변경 (위로 이동) - 섹션 간 이동 지원
   */
  const handleMoveUp = (menu: Menu) => {
    const allMenus = data?.data?.menus || [];
    // 모든 최상위 메뉴를 sort_order 기준으로 정렬
    const allTopLevelMenus = flattenAllTopLevelMenus(allMenus).sort((a, b) => a.sort_order - b.sort_order);

    const currentIndex = allTopLevelMenus.findIndex((m) => m.id === menu.id);
    if (currentIndex <= 0) return; // 이미 맨 위

    const prevMenu = allTopLevelMenus[currentIndex - 1];
    const orders = [
      { id: menu.id, sort_order: prevMenu.sort_order },
      { id: prevMenu.id, sort_order: menu.sort_order },
    ];

    updateOrderMutation.mutate(orders);
  };

  /**
   * 메뉴 순서 변경 (아래로 이동) - 섹션 간 이동 지원
   */
  const handleMoveDown = (menu: Menu) => {
    const allMenus = data?.data?.menus || [];
    // 모든 최상위 메뉴를 sort_order 기준으로 정렬
    const allTopLevelMenus = flattenAllTopLevelMenus(allMenus).sort((a, b) => a.sort_order - b.sort_order);

    const currentIndex = allTopLevelMenus.findIndex((m) => m.id === menu.id);
    if (currentIndex < 0 || currentIndex >= allTopLevelMenus.length - 1) return; // 이미 맨 아래

    const nextMenu = allTopLevelMenus[currentIndex + 1];
    const orders = [
      { id: menu.id, sort_order: nextMenu.sort_order },
      { id: nextMenu.id, sort_order: menu.sort_order },
    ];

    updateOrderMutation.mutate(orders);
  };

  /**
   * 계층 구조 메뉴 렌더링 (재귀)
   */
  const renderMenuRow = (menu: Menu, level: number = 0): React.ReactNode => {
    const hasChildren = menu.children && menu.children.length > 0;
    const isExpanded = expandedMenus.has(menu.id);
    const indent = level * 30;
    const IconComponent = menu.icon ? getIcon(menu.icon) : null;

    return (
      <React.Fragment key={menu.id}>
        <tr>
          <td style={{ paddingLeft: `${indent}px` }}>
            <div className="d-flex align-items-center">
              {hasChildren ? (
                <Button
                  variant="link"
                  className="p-0 me-2"
                  onClick={() => toggleMenu(menu.id)}
                  style={{ minWidth: '24px', height: '24px', color: '#fff' }}
                >
                  {isExpanded ? '▼' : '▶'}
                </Button>
              ) : (
                <span style={{ width: '24px', display: 'inline-block' }} />
              )}
              {IconComponent && (
                <span className="me-2" style={{ fontSize: '1.2rem' }}>
                  {React.createElement(IconComponent as React.ComponentType<any>)}
                </span>
              )}
              <span style={{ color: '#fff' }}>{menu.label}</span>
            </div>
          </td>
          <td>
            <span style={{ color: '#ccc', fontSize: '0.9em' }}>{menu.path}</span>
          </td>
          <td>
            <Badge bg="secondary">{getSectionLabel(menu.section)}</Badge>
          </td>
          <td>
            <span style={{ color: '#ccc' }}>{menu.icon || '-'}</span>
          </td>
          <td>
            <span style={{ color: '#ccc' }}>{menu.sort_order}</span>
          </td>
          <td>
            <Badge bg={menu.is_active ? 'success' : 'secondary'}>
              {menu.is_active ? '활성' : '비활성'}
            </Badge>
          </td>
          <td>
            <div className="d-flex gap-2">
              {/* 순서 변경 버튼 (최상위 메뉴만) */}
              {!menu.parent_id && (
                <>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleMoveUp(menu)}
                    disabled={updateOrderMutation.isPending}
                    title="위로 이동"
                  >
                    ↑
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleMoveDown(menu)}
                    disabled={updateOrderMutation.isPending}
                    title="아래로 이동"
                  >
                    ↓
                  </Button>
                </>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleEdit(menu)}
                title="수정"
              >
                수정
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteClick(menu)}
                title="삭제"
              >
                삭제
              </Button>
            </div>
          </td>
        </tr>
        {hasChildren && isExpanded && menu.children?.map((child: Menu) => renderMenuRow(child, level + 1))}
      </React.Fragment>
    );
  };

  // 권한 확인 (본사는 role 사용)
  if (currentAdmin?.role !== 'super_admin') {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <p style={{ color: '#ef4444' }}>권한이 없습니다. super_admin만 접근할 수 있습니다.</p>
        </div>
      </AdminLayout>
    );
  }

  const menus: Menu[] = data?.data?.menus || [];

  // 섹션별로 그룹화
  const menusBySection: { [key: string]: Menu[] } = {};
  menus.forEach((menu) => {
    if (!menu.parent_id) {
      // 최상위 메뉴만 섹션별로 그룹화
      if (!menusBySection[menu.section]) {
        menusBySection[menu.section] = [];
      }
      menusBySection[menu.section].push(menu);
    }
  });

  // 섹션별로 정렬 (sort_order 기준)
  Object.keys(menusBySection).forEach((section) => {
    menusBySection[section].sort((a, b) => a.sort_order - b.sort_order);
  });

  // 섹션 목록 (첫 번째 메뉴의 sort_order 기준으로 정렬)
  const sectionList = Object.keys(menusBySection).sort((a, b) => {
    const aFirstMenu = menusBySection[a][0];
    const bFirstMenu = menusBySection[b][0];
    return (aFirstMenu?.sort_order || 0) - (bFirstMenu?.sort_order || 0);
  });

  // 부모 메뉴 옵션 (최상위 메뉴만)
  const getParentMenuOptions = (currentMenuId?: number): Menu[] => {
    const allMenus: Menu[] = [];
    const flatten = (menuList: Menu[]) => {
      menuList.forEach((menu) => {
        if (menu.id !== currentMenuId && !menu.parent_id) {
          allMenus.push(menu);
        }
        if (menu.children) {
          flatten(menu.children);
        }
      });
    };
    flatten(menus);
    return allMenus;
  };

  return (
    <AdminLayout>
      <div>
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>메뉴 관리</h1>
              <p>관리자 시스템 메뉴를 관리합니다.</p>
            </div>
            <Button variant="primary" onClick={handleCreate}>
              메뉴 추가
            </Button>
          </div>
        </div>

        {/* 필터 영역 */}
        <Card className="mb-4" style={{ backgroundColor: '#3A4B5C', border: 'none' }}>
          <Card.Body>
            <Form.Group>
              <Form.Label style={{ color: '#fff' }}>섹션</Form.Label>
              <Form.Select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
              >
                <option value="">전체</option>
                <option value="DASHBOARD">대시보드</option>
                <option value="MANAGEMENT">관리</option>
                <option value="CONTENT">콘텐츠</option>
                <option value="SYSTEM">시스템</option>
              </Form.Select>
            </Form.Group>
          </Card.Body>
        </Card>

        {/* 메뉴 목록 테이블 */}
        <Card style={{ backgroundColor: '#3A4B5C', border: 'none' }}>
          <Card.Body>
            {isLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">로딩 중...</span>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-5" style={{ color: '#ef4444' }}>데이터를 불러올 수 없습니다.</div>
            ) : sectionList.length === 0 ? (
              <div className="text-center py-5" style={{ color: '#ccc' }}>메뉴가 없습니다.</div>
            ) : (
              <Table hover variant="dark">
                <thead>
                  <tr>
                    <th>메뉴명</th>
                    <th>경로</th>
                    <th>섹션</th>
                    <th>아이콘</th>
                    <th>정렬 순서</th>
                    <th>상태</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionList.map((section, sectionIndex) => {
                    const sectionMenus = menusBySection[section];
                    const isFirstSection = sectionIndex === 0;
                    const isLastSection = sectionIndex === sectionList.length - 1;

                    return (
                      <React.Fragment key={section}>
                        {/* 섹션 헤더 */}
                        <tr className="section-header-row" style={{ backgroundColor: 'rgba(0, 188, 212, 0.1)' }}>
                          <td colSpan={7}>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center gap-2">
                                <strong style={{ color: '#00BCD4' }}>{getSectionLabel(section)}</strong>
                                <Badge bg="secondary">{sectionMenus.length}개 메뉴</Badge>
                              </div>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleSectionMoveUp(section)}
                                  disabled={isFirstSection || updateSectionOrderMutation.isPending}
                                  title="섹션 위로 이동"
                                >
                                  ↑
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleSectionMoveDown(section)}
                                  disabled={isLastSection || updateSectionOrderMutation.isPending}
                                  title="섹션 아래로 이동"
                                >
                                  ↓
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {/* 섹션 내 메뉴들 */}
                        {sectionMenus.map((menu) => renderMenuRow(menu))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* 삭제 확인 모달 */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>메뉴 삭제 확인</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            정말로 이 메뉴를 삭제하시겠습니까?
            <br />
            <strong>{menuToDelete?.label} ({menuToDelete?.path})</strong>
            <br />
            {menuToDelete?.children && menuToDelete.children.length > 0 && (
              <small className="text-danger">
                하위 메뉴가 있는 경우 삭제할 수 없습니다.
              </small>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              취소
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '삭제 중...' : '삭제'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* 생성/수정 모달 */}
        <Modal show={showEditModal} onHide={() => { setShowEditModal(false); resetForm(); }} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingMenu ? '메뉴 수정' : '메뉴 추가'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>섹션 *</Form.Label>
                <Form.Select
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value as any })}
                  required
                >
                  <option value="DASHBOARD">대시보드</option>
                  <option value="MANAGEMENT">관리</option>
                  <option value="CONTENT">콘텐츠</option>
                  <option value="SYSTEM">시스템</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>부모 메뉴</Form.Label>
                <Form.Select
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value ? parseInt(e.target.value) : null })}
                >
                  <option value="">최상위 메뉴</option>
                  {getParentMenuOptions(editingMenu?.id).map((menu) => (
                    <option key={menu.id} value={menu.id}>
                      {menu.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>경로 *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.path}
                  onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                  placeholder="/admin/dashboard"
                  required
                />
                <Form.Text className="text-muted">/admin/로 시작해야 합니다.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>라벨 *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="대시보드"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>아이콘</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.icon || ''}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value || null })}
                  placeholder="MdDashboard"
                />
                <Form.Text className="text-muted">react-icons/md 아이콘 이름 (예: MdDashboard, MdSettings)</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>정렬 순서</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="활성화"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>
                취소
              </Button>
              <Button variant="primary" type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {createMutation.isPending || updateMutation.isPending ? '저장 중...' : '저장'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default Menus;
